import { useLazyGetActiveUsersQuery, ActiveUserData, useLazyPostUserDeactivateQuery } from '@/api';
import { AccessControlAction } from '@/constants';
import { useAccessControl } from '@/hooks';
import {
  selectActiveUsersList,
  selectActiveUsersPagination,
  selectActiveUsersRowCount,
  selectUsersFinding,
  setActiveUsersPagination,
  setUserEditId,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { TrashIcon, PencilIcon, UserAvatar } from '@repo/atomic-components';
import { OutlineIconButton } from '@/ui/components/OutlineIconButton';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { format } from 'date-fns';
import { classNames } from 'primereact/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router';

export const useActiveUsersListTable = () => {
  const dispatch = useAppDispatch();
  const context = useOutletContext();

  const activeUsersList = useAppSelector(selectActiveUsersList);
  const activeUsersRowCount = useAppSelector(selectActiveUsersRowCount);
  const activeUsersPagination = useAppSelector(selectActiveUsersPagination);

  const usersFinding = useAppSelector(selectUsersFinding);

  const isAddEditUsers = useAccessControl(AccessControlAction.ADD_EDIT_USERS);

  const [triggerActiveUsers, { isFetching: getActiveUsersFetching }] = useLazyGetActiveUsersQuery();
  const [triggerPostUserDeactivate] = useLazyPostUserDeactivateQuery();

  /**
   * Refetch data when pagination, filters or sorting change
   */
  useEffect(() => {
    triggerActiveUsers(
      {
        pageIndex: activeUsersPagination.pageIndex,
        pageSize: activeUsersPagination.pageSize,
        name: usersFinding.length ? usersFinding : undefined,
      },
      false,
    );
  }, [activeUsersPagination.pageIndex, activeUsersPagination.pageSize, triggerActiveUsers, usersFinding]);

  const handleEditUserClick = useCallback(
    (userId: string) => {
      if (
        context &&
        typeof context === 'object' &&
        'handleCreateOrEditNewUser' in context &&
        typeof context.handleCreateOrEditNewUser === 'function'
      ) {
        dispatch(setUserEditId(userId));
        context?.handleCreateOrEditNewUser();
      }
    },
    [context, dispatch],
  );

  const handleDeactivateUserClick = useCallback(
    (userId: string) => {
      triggerPostUserDeactivate({ userToEditId: userId });
    },
    [triggerPostUserDeactivate],
  );

  const columns = useMemo<ColumnDef<ActiveUserData, unknown>[]>(
    () => [
      {
        accessorKey: 'userName',
        header: 'Name',
        cell: (info) => {
          const userName = info.getValue() as string;
          if (!userName) return '';
          return (
            <div className="flex items-center gap-2">
              <UserAvatar name={userName} size={24} fontSize={10} />
              <span
                className={classNames('block max-w-[300px] overflow-hidden overflow-ellipsis whitespace-nowrap')}
                title={userName}
              >
                {userName}
              </span>
            </div>
          );
        },
        size: 300,
      },
      {
        accessorKey: 'userRole',
        header: 'Role',
        cell: (info) => (
          <>
            {info.getValue() ? (
              <span
                className={classNames('block max-w-[300px] overflow-hidden overflow-ellipsis whitespace-nowrap')}
                title={info.getValue() as string}
              >
                {info.getValue() as string}
              </span>
            ) : (
              ''
            )}
          </>
        ),
        size: 300,
      },
      {
        accessorKey: 'userEmail',
        header: 'Email address',
        cell: (info) => (
          <>
            {info.getValue() ? (
              <span
                className={classNames('block max-w-[300px] overflow-hidden overflow-ellipsis whitespace-nowrap')}
                title={info.getValue() as string}
              >
                {info.getValue() as string}
              </span>
            ) : (
              ''
            )}
          </>
        ),
        size: 300,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created date',
        cell: (info) => (
          <>
            {info.getValue() ? (
              <span
                className={classNames('block max-w-[300px] overflow-hidden overflow-ellipsis whitespace-nowrap')}
                title={format(new Date(info.getValue() as string), 'd MMM y')}
              >
                {format(new Date(info.getValue() as string), 'd MMM y')}
              </span>
            ) : (
              ''
            )}
          </>
        ),
        size: 200,
      },
      {
        header: '',
        id: 'actions',
        cell: (info) => {
          const rowValue = info.row.original;

          return (
            <div className="flex flex-nowrap items-center justify-end gap-2 opacity-0 transition-opacity group-hover:opacity-100">
              {isAddEditUsers && (
                <OutlineIconButton
                  className="!bg-white"
                  icon={<PencilIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditUserClick(rowValue.userId);
                  }}
                  aria-label="Edit user"
                  title="Edit user"
                />
              )}
              {isAddEditUsers && (
                <OutlineIconButton
                  className="!bg-white"
                  icon={<TrashIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeactivateUserClick(rowValue.userId);
                  }}
                  aria-label="Deactivate user"
                  title="Deactivate user"
                />
              )}
            </div>
          );
        },
      },
    ],

    [handleDeactivateUserClick, handleEditUserClick, isAddEditUsers],
  );

  const table = useReactTable({
    data: activeUsersList,
    columns,
    getCoreRowModel: getCoreRowModel(),

    state: {
      pagination: activeUsersPagination,
    },

    //** START PAGINATION */
    rowCount: activeUsersRowCount,
    onPaginationChange: (updaterOrValue) => {
      if (typeof updaterOrValue === 'function') {
        const newPaginationState = updaterOrValue(activeUsersPagination);
        dispatch(setActiveUsersPagination(newPaginationState));
      } else {
        dispatch(setActiveUsersPagination(updaterOrValue));
      }
    },
    manualPagination: true,
    //** END PAGINATION */
  });

  return {
    table,
    isTableDataLoading: getActiveUsersFetching,
  };
};
